import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../../components/layout/PublicLayout';
import Card from '../../components/common/Card';
import Loader from '../../components/common/Loader';
import ErrorMessage from '../../components/common/ErrorMessage';
import contestService from '../../api/services/contestService';
import { handleApiError, formatDate, formatCurrency } from '../../utils/helpers';
import { getImageUrl } from '../../utils/imageHelper';
import { useTheme } from '../../context/ThemeContext';

const ContestsPage = () => {
  const { dark } = useTheme();
  const d = dark;

  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchContests();
  }, []);

  const fetchContests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await contestService.getPublicContests();
      if (response.success) {
        setContests(response.data);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className={`min-h-screen transition-colors duration-500 ${d ? 'bg-gray-950' : 'bg-gray-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <Loader size="lg" text="Loading contests..." />
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
        .cp * { font-family: 'Plus Jakarta Sans', sans-serif !important; }

        @keyframes cp-up   { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes cp-fade { from{opacity:0} to{opacity:1} }
        @keyframes cp-card { from{opacity:0;transform:translateY(32px) scale(.97)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes cp-shine {
          0%   { transform: translateX(-100%) skewX(-15deg); }
          100% { transform: translateX(250%) skewX(-15deg); }
        }
        @keyframes cp-pulse-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(.75)} }
        @keyframes cp-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes cp-grad { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }

        .cp .cu  { animation: cp-up   .65s cubic-bezier(.22,1,.36,1) both; }
        .cp .cf  { animation: cp-fade .5s ease both; }
        .cp .cc  { animation: cp-card .6s cubic-bezier(.22,1,.36,1) both; }

        .cp .d1  { animation-delay:.05s }
        .cp .d2  { animation-delay:.10s }
        .cp .d3  { animation-delay:.15s }
        .cp .d4  { animation-delay:.20s }
        .cp .d5  { animation-delay:.25s }
        .cp .d6  { animation-delay:.30s }
        .cp .d7  { animation-delay:.35s }
        .cp .d8  { animation-delay:.40s }
        .cp .d9  { animation-delay:.45s }

        /* Gradient text */
        .cp .grad-text {
          background: linear-gradient(135deg, #ef4444 0%, #f97316 40%, #ef4444 70%, #dc2626 100%);
          background-size: 300% 300%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: cp-grad 4s ease infinite;
        }

        /* Contest card */
        .cp .contest-card {
          transition: all .35s cubic-bezier(.22,1,.36,1);
          position: relative;
        }
        .cp .contest-card:hover {
          transform: translateY(-10px);
        }

        /* Shine sweep on hover */
        .cp .contest-card .shine {
          position: absolute;
          inset: 0;
          overflow: hidden;
          border-radius: inherit;
          pointer-events: none;
          z-index: 2;
        }
        .cp .contest-card .shine::after {
          content: '';
          position: absolute;
          top: 0; left: 0;
          width: 40%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.08), transparent);
          transform: translateX(-100%) skewX(-15deg);
        }
        .cp .contest-card:hover .shine::after {
          animation: cp-shine .55s ease forwards;
        }

        /* Banner image zoom */
        .cp .banner-img {
          transition: transform .5s cubic-bezier(.22,1,.36,1);
        }
        .cp .contest-card:hover .banner-img {
          transform: scale(1.08);
        }

        /* Pulse dot */
        .cp .pulse-dot {
          animation: cp-pulse-dot 1.8s ease-in-out infinite;
        }

        /* Vote price badge */
        .cp .price-badge {
          background: linear-gradient(135deg, #ef4444, #dc2626);
          background-size: 200% 200%;
          animation: cp-grad 3s ease infinite;
        }

        /* Status pill */
        .cp .status-active {
          background: rgba(34,197,94,.15);
          color: #22c55e;
          border: 1px solid rgba(34,197,94,.3);
        }
        .cp .status-other {
          background: rgba(156,163,175,.15);
          color: #9ca3af;
          border: 1px solid rgba(156,163,175,.2);
        }

        /* Empty state float */
        .cp .empty-icon { animation: cp-float 3s ease-in-out infinite; }

        /* Grid bg */
        .cp .grid-bg-dark {
          background-image:
            linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px);
          background-size: 56px 56px;
        }
        .cp .grid-bg-light {
          background-image:
            linear-gradient(rgba(0,0,0,.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,.04) 1px, transparent 1px);
          background-size: 56px 56px;
        }

        /* Section label pill */
        .cp .pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .1em;
          text-transform: uppercase;
        }

        /* Arrow on hover */
        .cp .arrow-icon {
          transition: transform .25s;
        }
        .cp .contest-card:hover .arrow-icon {
          transform: translateX(4px);
        }
      `}</style>

      <div className={`cp transition-colors duration-500 ${d ? 'bg-gray-950' : 'bg-gray-50'}`}>
        <PublicLayout>
          <div className="relative">

            {/* Background grid */}
            <div className={`absolute inset-0 pointer-events-none ${d ? 'grid-bg-dark' : 'grid-bg-light'}`} />

            {/* Top radial glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-64 rounded-full blur-3xl pointer-events-none opacity-20"
              style={{ background: 'radial-gradient(circle, rgba(239,68,68,.6), transparent 70%)' }} />

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">

              {/* ── Header ── */}
              <div className="text-center mb-16">
                {/* <div className={`cu d1 pill mb-5 ${d ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 pulse-dot inline-block" />
                  Live Now
                </div> */}

                <h1 className="cu d2 text-4xl sm:text-5xl md:text-6xl font-black leading-tight mb-4">
                  <span className={d ? 'text-white' : 'text-gray-900'}>Browse </span>
                  <span className="grad-text">Contests</span>
                </h1>

                <p className={`cu d3 text-lg sm:text-xl max-w-xl mx-auto ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                  Vote for your favorite contestants in active contests
                </p>

                {/* Count badge */}
                {contests.length > 0 && (
                  <div className={`cu d4 inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-xl text-sm font-semibold ${d ? 'bg-white/5 border border-white/10 text-gray-300' : 'bg-white border border-gray-200 text-gray-600 shadow-sm'}`}>
                    <span className="w-2 h-2 rounded-full bg-green-500 pulse-dot inline-block" />
                    {contests.length} active contest{contests.length !== 1 ? 's' : ''} running
                  </div>
                )}
              </div>

              {/* ── Error ── */}
              {error && (
                <ErrorMessage message={error} onRetry={fetchContests} className="mb-8" />
              )}

              {/* ── Empty state ── */}
              {contests.length === 0 ? (
                <div className={`cf rounded-3xl border p-16 text-center ${d ? 'bg-gray-900/60 border-white/8' : 'bg-white border-gray-100 shadow-sm'}`}>
                  <div className="empty-icon w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center shadow-xl shadow-red-600/30">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className={`text-2xl font-black mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>No contests yet</h3>
                  <p className={`text-base ${d ? 'text-gray-400' : 'text-gray-500'}`}>Check back later for new contests</p>
                </div>
              ) : (
                /* ── Contest Grid ── */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                  {contests.map((contest, i) => (
                    <ContestCard key={contest.id} contest={contest} dark={d} index={i} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </PublicLayout>
      </div>
    </>
  );
};

// ── Contest Card ───────────────────────────────────────────────────────────────
const ContestCard = ({ contest, dark: d, index }) => {
  const [imageError, setImageError] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const delayClass = `d${Math.min(index + 1, 9)}`;
  const isActive = contest.status === 'ACTIVE';

  return (
    <Link to={`/contests/${contest.id}`} className="group block">
      <div className={`contest-card cc ${delayClass} h-full rounded-2xl overflow-hidden border ${d
        ? 'bg-gray-900 border-white/8 hover:border-red-500/50 hover:shadow-2xl hover:shadow-red-600/15'
        : 'bg-white border-gray-100 hover:border-red-300 hover:shadow-2xl hover:shadow-red-100'
      }`}>

        {/* Shine overlay */}
        <div className="shine" />

        {/* ── Banner ── */}
        <div className="relative w-full h-52 overflow-hidden">
          {contest.banner && !imageError ? (
            <img
              src={getImageUrl(contest.banner)}
              alt={contest.title}
              className="banner-img w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-500 via-red-600 to-red-800">
              {/* Abstract pattern behind fallback */}
              <div className="absolute inset-0 opacity-20" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,.4) 1px, transparent 0)',
                backgroundSize: '24px 24px',
              }} />
              <svg className="w-16 h-16 text-white/50 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}

          {/* Dark gradient overlay at bottom */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

          {/* Status badge */}
          <div className="absolute top-3 right-3 z-10">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${isActive ? 'status-active' : 'status-other'}`}>
              {isActive && <span className="w-1.5 h-1.5 rounded-full bg-green-500 pulse-dot inline-block" />}
              {contest.status}
            </span>
          </div>

          {/* Vote price badge — bottom left over image */}
          <div className="absolute bottom-3 left-3 z-10">
            <div className="price-badge px-3 py-1.5 rounded-xl text-white text-xs font-black shadow-lg">
              {contest.votePrice > 0 ? formatCurrency(contest.votePrice) : 'FREE'} / vote
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="p-5">

          {/* Host row */}
          <div className={`flex items-center gap-3 mb-4 pb-4 border-b ${d ? 'border-white/8' : 'border-gray-100'}`}>
            {contest.host?.logo && !logoError ? (
              <img
                src={getImageUrl(contest.host.logo)}
                alt={contest.host.name}
                className={`w-9 h-9 rounded-full object-cover flex-shrink-0 border-2 ${d ? 'border-white/15' : 'border-red-100'}`}
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center flex-shrink-0 shadow-md">
                <span className="text-white font-black text-sm">{contest.host?.name?.charAt(0) || 'H'}</span>
              </div>
            )}
            <div className="min-w-0">
              <p className={`text-xs font-semibold truncate ${d ? 'text-gray-300' : 'text-gray-700'}`}>{contest.host?.name}</p>
              <p className={`text-[11px] ${d ? 'text-gray-500' : 'text-gray-400'}`}>Host Organization</p>
            </div>
          </div>

          {/* Title */}
          <h3 className={`text-lg font-black mb-2 leading-snug transition-colors duration-300 group-hover:text-red-500 ${d ? 'text-white' : 'text-gray-900'}`}>
            {contest.title}
          </h3>

          {/* Description */}
          <p className={`text-sm mb-4 line-clamp-2 leading-relaxed ${d ? 'text-gray-400' : 'text-gray-500'}`}>
            {contest.description}
          </p>

          {/* Stats row */}
          <div className={`flex items-center gap-3 mb-4`}>
            <div className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl ${d ? 'bg-white/5' : 'bg-gray-50'}`}>
              <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <div>
                <p className={`text-[10px] font-semibold uppercase tracking-wide ${d ? 'text-gray-500' : 'text-gray-400'}`}>Categories</p>
                <p className={`text-sm font-black ${d ? 'text-white' : 'text-gray-900'}`}>{contest._count?.categories || 0}</p>
              </div>
            </div>
            <div className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl ${d ? 'bg-white/5' : 'bg-gray-50'}`}>
              <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div>
                <p className={`text-[10px] font-semibold uppercase tracking-wide ${d ? 'text-gray-500' : 'text-gray-400'}`}>Ends</p>
                <p className={`text-sm font-black ${d ? 'text-white' : 'text-gray-900'}`}>{formatDate(contest.endDate)}</p>
              </div>
            </div>
          </div>

          {/* CTA row */}
          <div className={`flex items-center justify-between pt-4 border-t ${d ? 'border-white/8' : 'border-gray-100'}`}>
            <div className={`text-xs font-semibold uppercase tracking-wide ${d ? 'text-gray-500' : 'text-gray-400'}`}>
              Vote Price
            </div>
            <div className="flex items-center gap-3">
              <span className="text-lg font-black text-red-500">
                {contest.votePrice > 0 ? formatCurrency(contest.votePrice) : 'Free'}
              </span>
              <div className="flex items-center gap-1 bg-gradient-to-r from-red-600 to-red-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-md shadow-red-600/25 group-hover:shadow-red-600/40 transition-shadow">
                Vote Now
                <svg className="w-3.5 h-3.5 arrow-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ContestsPage;